import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ContextType,
  type ReactNode,
} from "react";
import { ChevronRight, Hand, MousePointer2 } from "lucide-react";
import { Button } from "../atoms/Button/Button.js";
import { Tooltip } from "../molecules/Tooltip/Tooltip.js";
import type { CanvasNavigationContext } from "../lib/canvas-navigation-context.js";
import { MOTION_DURATION_MS } from "../motion.js";

/** Keeps one stable selected-tool anchor while the remaining controls unfold. */
export function ViewportControlsReveal({
  navigation,
  children,
}: {
  navigation: NonNullable<ContextType<typeof CanvasNavigationContext>>;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const anchor = useRef<HTMLButtonElement>(null);
  const hovered = useRef(false);
  const keyboardFocus = useRef(false);
  const pointerFocus = useRef(false);
  const touchPinned = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const panelId = useId();
  const cancelClose = useCallback(() => clearTimeout(timer.current), []);
  const reveal = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);
  const scheduleClose = useCallback(() => {
    cancelClose();
    timer.current = setTimeout(() => {
      if (!hovered.current && !keyboardFocus.current && !touchPinned.current)
        setOpen(false);
    }, MOTION_DURATION_MS.layout);
  }, [cancelClose]);

  useLayoutEffect(() => {
    panel.current?.toggleAttribute("inert", !open);
  }, [open]);
  useLayoutEffect(() => {
    const node = content.current;
    if (!node) return;
    const measure = () => setWidth(node.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [open]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !root.current?.contains(event.target)
      ) {
        cancelClose();
        hovered.current = false;
        keyboardFocus.current = false;
        touchPinned.current = false;
        // A tap on a non-focusable canvas must not leave focus in hidden chrome.
        if (
          document.activeElement instanceof HTMLElement &&
          root.current?.contains(document.activeElement)
        )
          document.activeElement.blur();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", outside);
    return () => {
      document.removeEventListener("pointerdown", outside);
      cancelClose();
    };
  }, [cancelClose]);

  const isPan = navigation.tool === "pan";
  const SelectedIcon = isPan ? Hand : MousePointer2;
  const OtherIcon = isPan ? MousePointer2 : Hand;
  const selectedName = isPan ? "Pan canvas" : "Select";
  const otherName = isPan ? "Select" : "Pan canvas";
  return (
    // Passive interaction boundary; buttons own all keyboard actions.
    <div
      ref={root}
      className="flex items-center"
      data-viewport-expanded={open}
      onPointerEnter={(event) => {
        if (event.pointerType !== "touch") {
          touchPinned.current = false;
          hovered.current = true;
          reveal();
        }
      }}
      onPointerLeave={() => {
        hovered.current = false;
        scheduleClose();
      }}
      onPointerDownCapture={(event) => {
        pointerFocus.current = true;
        keyboardFocus.current = false;
        if (event.pointerType === "touch") {
          touchPinned.current = true;
          reveal();
        }
      }}
      onPointerUp={() => {
        pointerFocus.current = false;
      }}
      onFocusCapture={() => {
        if (!pointerFocus.current) keyboardFocus.current = true;
        reveal();
      }}
      onBlurCapture={(event) => {
        if (
          !(event.relatedTarget instanceof Node) ||
          !root.current?.contains(event.relatedTarget)
        ) {
          keyboardFocus.current = false;
          scheduleClose();
        }
      }}
      onKeyDownCapture={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          cancelClose();
          pointerFocus.current = true;
          anchor.current?.focus();
          pointerFocus.current = false;
          keyboardFocus.current = false;
          hovered.current = false;
          touchPinned.current = false;
          setOpen(false);
        } else {
          keyboardFocus.current = true;
          reveal();
        }
      }}
    >
      <Tooltip
        content={
          open
            ? isPan
              ? "Pan canvas (hold Space)"
              : "Select"
            : `${selectedName} · Show canvas controls`
        }
      >
        <Button
          ref={anchor}
          size="sm"
          variant="ghost"
          pressed
          aria-label={selectedName}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={reveal}
        >
          <SelectedIcon className="h-3.5 w-3.5" aria-hidden />
          <ChevronRight
            aria-hidden
            className="h-3 w-3 motion-safe:transition-transform motion-safe:duration-motion-layout motion-safe:ease-motion-standard"
            style={{ transform: open ? "rotate(180deg)" : undefined }}
          />
        </Button>
      </Tooltip>
      <div
        ref={panel}
        id={panelId}
        aria-hidden={!open}
        // Keep the focus outline inside the clipping boundary during reveal.
        className="overflow-hidden [&_button:focus-visible]:-outline-offset-2 motion-safe:transition-[width,opacity] motion-safe:duration-motion-layout motion-safe:ease-motion-standard"
        style={{ width: open ? width : 0, opacity: open ? 1 : 0 }}
      >
        <div ref={content} className="flex w-max items-center gap-0.5 ps-0.5">
          <Tooltip
            content={isPan ? "Select" : "Pan canvas (hold Space)"}
            disabled={!open}
            followAnchor
          >
            <Button
              size="sm"
              variant="ghost"
              pressed={false}
              aria-label={otherName}
              onClick={() => navigation.selectTool(isPan ? "select" : "pan")}
            >
              <OtherIcon className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </Tooltip>
          <span aria-hidden className="mx-0.5 h-4 w-px bg-pb-border" />
          {children}
        </div>
      </div>
    </div>
  );
}
